import { useEffect, useState } from 'react';
import { ContractsReadinessStatus } from '@dealstack/shared/lib/common/business/contracts';
import { MAX_GENERATION_WAIT_MS } from '@dealstack/frontend/investor/pages/Investment/common/DocumentWorkflowContracts/utils';

/**
 * True when documents have sat in "preparing" longer than they should have.
 *
 * The investor used to learn this as a side effect of the generation poll
 * giving up after 25 seconds. That poll is gone — generation completion now
 * arrives as an SSE signal — but the escape hatch it provided still matters:
 * with no fallback, an investor whose signal never lands would otherwise sit on
 * "preparing" until the stream rolls over 15-20 minutes later. This drives the
 * retry affordance that prevents that.
 *
 * Re-arms on each fresh entry into NOT_READY, so a later regeneration gets its
 * own window rather than inheriting a stale one.
 */
export const useGenerationTimeout = (status: ContractsReadinessStatus | undefined): boolean => {
	const [isGenerationTimedOut, setIsGenerationTimedOut] = useState(false);

	useEffect(() => {
		if (status !== ContractsReadinessStatus.NOT_READY) {
			setIsGenerationTimedOut(false);
			return undefined;
		}

		setIsGenerationTimedOut(false);
		const timeoutId = window.setTimeout(
			() => setIsGenerationTimedOut(true),
			MAX_GENERATION_WAIT_MS
		);
		return () => window.clearTimeout(timeoutId);
	}, [status]);

	return isGenerationTimedOut;
};
